import { Toast } from '@components/Toast';
import { faCopy, faPencil } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfigurationContext } from '@partful/features/configurations/lib/configurationProvider';
import { useDuplicateInstructionVersion } from '@partful/features/instructions/versions/lib/useDuplicateInstructionVersion';
import { useUpdateInstructionVersion } from '@partful/features/instructions/versions/lib/useUpdateInstructionVersion';
import { VersionModel } from '@partful/instructions-domain-models';
import { Badge, BadgeProps } from '@partful/shared/ui/Badge';
import { Button } from '@partful/shared/ui/Button';
import { Surface } from '@partful/shared/ui/Surface';
import { Typography } from '@partful/shared/ui/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styles from './VersionCard.module.scss';

type VersionCardProps = {
  version: VersionModel;
};

const eventNames: Partial<Record<VersionModel['status'], string>> = {
  draft: 'instructions_versionlist_publish_click',
  published: 'instructions_versionlist_to_draft_click',
};

export function VersionCard({
  version: {
    id,
    instructionId,
    versionNumber,
    status,
    description,
    authorName,
    authorUserId,
    cadFileName,
    cadUploadDate,
    failureReason,
    __lastUpdatedAt,
    __version,
  },
  ...rest
}: Readonly<VersionCardProps>) {
  const { t: i18n } = useTranslation();
  const configuration = useConfigurationContext();

  const badgeVariant = useMemo<BadgeProps['variant']>(() => {
    switch (status) {
      case 'published': {
        return 'success';
      }
      case 'duplicating': {
        return 'info';
      }
      case 'failed': {
        return 'error';
      }
      default: {
        return 'default';
      }
    }
  }, [status]);

  const canInteract = useMemo(() => status === 'draft' || status === 'published', [status]);

  const {
    mutate: updateInstructionVersion,
    isLoading: isPublishing,
    isError: isPublishingError,
    isSuccess: isPublishingSuccess,
  } = useUpdateInstructionVersion();

  const handlePublish = () => {
    updateInstructionVersion({
      instructionId,
      versionId: id,
      updateArgs: {
        __expectedVersion: __version,
        status: status === 'draft' ? 'published' : 'draft',
      },
    });
  };

  const {
    mutate: duplicateInstructionVersion,
    isLoading: isDuplicating,
    isError: isDuplicatingError,
    isSuccess: isDuplicateSuccess,
  } = useDuplicateInstructionVersion();

  const handleDuplicate = () => {
    duplicateInstructionVersion({
      instructionId,
      versionId: id,
      duplicateArgs: {
        authorName,
        authorUserId,
      },
    });
  };

  const dateFormat = Intl.DateTimeFormat(void 0, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <Surface shadow="medium" className={styles.version} {...rest}>
      <div className={styles.header}>
        <div className={styles.name}>
          <Typography appearance="heading4">
            {i18n('instructions:version.data.versionNumber.label', { number: versionNumber })}
          </Typography>
          <Badge variant={badgeVariant}>
            {i18n(
              status === 'failed'
                ? [`instructions:version.status.failureReason.${failureReason}`, `instructions:version.status.failed`]
                : `instructions:version.status.${status}`
            )}
          </Badge>
        </div>
        {canInteract && (
          <div className={styles.actions}>
            <Button name={eventNames[status]} variant="primary" onClick={handlePublish} disabled={isPublishing}>
              {status === 'draft' &&
                i18n(isPublishing ? 'instructions:version.actions.publishing' : 'instructions:version.actions.publish')}
              {status === 'published' &&
                i18n(
                  isPublishing ? 'instructions:version.actions.unpublishing' : 'instructions:version.actions.unpublish'
                )}
            </Button>
            <Button
              name="instructions_versionlist_edit_click"
              variant="secondary"
              linkComponent={Link}
              to={`/instructions/manage/${configuration.PID}/versions/${id}?productName=${encodeURIComponent(
                configuration.name
              )}`}
            >
              <FontAwesomeIcon icon={faPencil} />
              {i18n('instructions:version.actions.edit')}
            </Button>
            <Button
              name="instructions_versionlist_duplicate_click"
              variant="secondary"
              onClick={handleDuplicate}
              disabled={isDuplicating}
            >
              <FontAwesomeIcon icon={faCopy} />
              {i18n(
                isDuplicating ? 'instructions:version.actions.duplicating' : 'instructions:version.actions.duplicate'
              )}
            </Button>
          </div>
        )}
      </div>
      <Typography as="p" whiteSpace="pre-line">
        {description}
      </Typography>
      <div className={styles.metadata}>
        <div>
          <Typography fontWeight={600}>{i18n('instructions:version.data.__lastUpdatedAt.label')}</Typography>
          <Typography>{dateFormat.format(new Date(__lastUpdatedAt))}</Typography>
        </div>
        <div>
          <Typography fontWeight={600}>{i18n('instructions:version.data.authorName.label')}</Typography>
          <Typography>{authorName}</Typography>
        </div>
        <div>
          <Typography fontWeight={600}>{i18n('instructions:version.data.cadFileName.label')}</Typography>
          <Typography>{cadFileName}</Typography>
        </div>
        <div>
          <Typography fontWeight={600}>{i18n('instructions:version.data.cadUploadDate.label')}</Typography>
          <Typography>{dateFormat.format(new Date(cadUploadDate))}</Typography>
        </div>
      </div>
      <Toast
        dependency={isPublishingError}
        severity="error"
        title={i18n(
          status === 'draft'
            ? 'instructions:version.actions.publishError.title'
            : 'instructions:version.actions.unpublishError.title'
        )}
        message={i18n(
          status === 'draft'
            ? 'instructions:version.actions.publishError.message'
            : 'instructions:version.actions.unpublishError.message'
        )}
        testId="publish-error-message"
      />
      <Toast
        dependency={isPublishingSuccess}
        severity="success"
        title={i18n(
          status === 'draft'
            ? 'instructions:version.actions.unpublishSuccess.title'
            : 'instructions:version.actions.publishSuccess.title'
        )}
        testId="publish-success-message"
      />
      <Toast
        dependency={isDuplicatingError}
        severity="error"
        title={`${i18n('instructions:version.actions.duplicateError.title')}`}
        message={`${i18n('instructions:version.actions.duplicateError.message')}`}
        testId="duplicate-error-message"
      />
      <Toast
        dependency={isDuplicateSuccess}
        severity="success"
        title={`${i18n('instructions:version.actions.duplicateSuccess.title')}`}
        testId="duplicate-success-message"
      />
    </Surface>
  );
}
